import { areObjectIdsEqual } from 'helpers/idComparison';
import { Deletable, Identificable } from 'shared/types/commonView';
import { findById } from 'shared/utils/id/findById';

/**
 * What the versioning rules read of a documentation: its versions and which
 * one is current. The backend points at the current version explicitly; the
 * newest upload is not current by itself (PT-7117).
 */
export interface VersionedDocumentation<TVersion extends Identificable & Deletable = Identificable & Deletable>
  extends Deletable {
  versions: TVersion[];
  /** `null`: the documentation has no current version (none uploaded, or it was cleared). */
  currentVersionId: string | null;
}

/** The version shown for the documentation; `undefined` without a current version or before it is stored. */
export function findCurrentVersion<TVersion extends Identificable & Deletable>(
  documentation: Pick<VersionedDocumentation<TVersion>, 'versions' | 'currentVersionId'>
): TVersion | undefined {
  const { currentVersionId, versions } = documentation;
  return currentVersionId ? findById(versions, currentVersionId) : undefined;
}

/**
 * One version of the documentation; `undefined` without an id (e.g. a documentation
 * without a current version) or before the version is stored. A missing id never
 * matches a version not sent yet, which has no id either.
 */
export function findVersionById<TVersion extends Identificable>(
  documentation: { versions: TVersion[] },
  versionId: string | null | undefined
): TVersion | undefined {
  return versionId ? documentation.versions.find(({ _id }) => areObjectIdsEqual(_id, versionId)) : undefined;
}

/**
 * Whether the version is the documentation's current one. A documentation without a
 * current version has none: the id helper reads two missing ids as equal, so a version
 * not sent yet (no id) would otherwise pass for the current one.
 */
export function isCurrentVersion(
  documentation: Pick<VersionedDocumentation, 'currentVersionId'>,
  version: Identificable
): boolean {
  const { currentVersionId } = documentation;
  return Boolean(currentVersionId) && areObjectIdsEqual(version._id, currentVersionId);
}

/** A version can be used (opened, linked, drawn on) while neither it nor its documentation is withdrawn. */
export function isVersionAvailable(documentation: Deletable, version: Deletable): boolean {
  return !documentation.deleted && !version.deleted;
}
